import { Link } from 'react-router-dom';

interface Article {
  id: number;
  title: string;
  category: string;
  excerpt: string;
  image: string;
  views: number;
  comments: number;
  tags: string[];
  publishDate: string;
  author: string;
  isHot?: boolean;
  isNew?: boolean;
  readTime: string;
}

const categoryLabelMap: Record<string, string> = {
  star: '明星八卦',
  variety: '综艺热瓜',
  'internet-celebrity': '网红爆料',
  expose: '黑料实锤',
  drama: '影视吃瓜',
  trending: '今日热搜',
};

function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return views.toString();
}

export default function ArticleCard({ article, variant = 'default' }: { article: Article; variant?: 'default' | 'compact' | 'featured' }) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block relative overflow-hidden rounded-xl cursor-pointer"
      >
        <div className="relative h-[420px] md:h-[480px] w-full">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/40 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <div className="flex items-center gap-2 mb-3">
              {article.isHot && (
                <span className="px-2 py-0.5 text-xs font-semibold bg-primary-500 text-background-50 rounded whitespace-nowrap">
                  热门
                </span>
              )}
              {article.isNew && (
                <span className="px-2 py-0.5 text-xs font-semibold bg-accent-500 text-background-950 rounded whitespace-nowrap">
                  新瓜
                </span>
              )}
              <span className="text-xs text-foreground-400">
                {categoryLabelMap[article.category] || article.category}
              </span>
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-50 mb-2 line-clamp-2 group-hover:text-primary-400 transition-colors">
              {article.title}
            </h2>
            <p className="text-sm text-foreground-400 line-clamp-2 mb-3">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formatViews(article.views)}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-message-3-line"></i>
                {article.comments}
              </span>
              <span>{article.publishDate}</span>
              <span>{article.readTime}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-3 p-3 rounded-lg hover:bg-background-800/30 transition-all cursor-pointer"
      >
        <div className="w-24 h-16 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-foreground-200 line-clamp-2 group-hover:text-primary-400 transition-colors leading-snug">
            {article.title}
          </h3>
          <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line text-[10px]"></i>
              {formatViews(article.views)}
            </span>
            <span>{article.publishDate}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block bg-background-900/80 rounded-xl overflow-hidden border border-background-800/50 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          {article.isHot && (
            <span className="px-2 py-0.5 text-xs font-semibold bg-primary-500 text-background-50 rounded whitespace-nowrap">
              热门
            </span>
          )}
          {article.isNew && (
            <span className="px-2 py-0.5 text-xs font-semibold bg-accent-500 text-background-950 rounded whitespace-nowrap">
              新瓜
            </span>
          )}
        </div>
        <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded bg-background-950/80 text-xs text-foreground-400 whitespace-nowrap">
          {article.readTime}
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-sm font-semibold text-foreground-200 line-clamp-2 mb-2 group-hover:text-primary-400 transition-colors leading-snug">
          {article.title}
        </h3>
        <p className="text-xs text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line text-[10px]"></i>
              {formatViews(article.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-message-3-line text-[10px]"></i>
              {article.comments}
            </span>
          </div>
          <span className="text-xs text-foreground-600">{article.publishDate}</span>
        </div>
      </div>
    </Link>
  );
}